import { useState, useEffect } from 'react';
import axios from 'axios';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const SponsorBanner = ({ 
  className = '', 
  variant = 'default',  // 'default', 'hero', 'top-banner'
  showTitle = true 
}) => {
  const [sponsors, setSponsors] = useState([]);

  useEffect(() => {
    const fetchSponsors = async () => {
      try {
        const response = await axios.get(`${API_URL}/sponsors`);
        setSponsors(response.data);
      } catch (error) {
        console.error('Failed to load sponsors:', error);
      }
    };
    fetchSponsors();
  }, []);

  if (sponsors.length === 0) return null;

  // Hero variant - Large display for landing page
  if (variant === 'hero') {
    return (
      <div className={`py-12 ${className}`} data-testid="sponsor-banner-hero">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-2xl sm:text-3xl font-heading text-white mb-2">
            Table Golf Pro Tour
          </h2>
          <p className="text-lg sm:text-xl text-primary mb-8 font-medium">
            Sponsored by {sponsors.map(s => s.name).join(' & ')}
          </p>
          <div className="flex flex-wrap items-center justify-center gap-8">
            {sponsors.map((sponsor) => (
              <a
                key={sponsor.id}
                href={sponsor.website_url || '#'}
                target={sponsor.website_url ? '_blank' : '_self'}
                rel="noopener noreferrer"
                className="group transition-transform hover:scale-105"
                title={sponsor.name}
                data-testid={`sponsor-logo-hero-${sponsor.id}`}
              >
                <div className="h-24 sm:h-32 md:h-40 w-48 sm:w-64 md:w-80 flex items-center justify-center">
                  <img 
                    src={sponsor.image_url} 
                    alt={sponsor.name}
                    className="max-h-full max-w-full object-contain drop-shadow-lg"
                  />
                </div>
              </a>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // Top banner variant - Colored banner at top of inner pages
  if (variant === 'top-banner') {
    return (
      <div 
        className={`bg-gradient-to-r from-primary/10 via-primary/5 to-secondary/10 border-b border-white/10 ${className}`}
        data-testid="sponsor-banner-top"
      >
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-6">
            {showTitle && (
              <div className="text-center sm:text-left">
                <p className="text-sm text-muted-foreground">Table Golf Pro Tour</p>
                <p className="text-primary font-medium">
                  Sponsored by {sponsors.map(s => s.name).join(' & ')}
                </p>
              </div>
            )}
            <div className="flex items-center gap-6">
              {sponsors.map((sponsor) => (
                <a
                  key={sponsor.id}
                  href={sponsor.website_url || '#'}
                  target={sponsor.website_url ? '_blank' : '_self'}
                  rel="noopener noreferrer"
                  className="group transition-transform hover:scale-105"
                  title={sponsor.name}
                  data-testid={`sponsor-logo-top-${sponsor.id}`}
                >
                  <div className="h-12 sm:h-16 w-24 sm:w-32 flex items-center justify-center">
                    <img 
                      src={sponsor.image_url} 
                      alt={sponsor.name}
                      className="max-h-full max-w-full object-contain"
                    />
                  </div>
                </a>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Default variant - Small footer style
  return (
    <div className={`py-6 ${className}`} data-testid="sponsor-banner-default">
      <p className="text-center text-muted-foreground text-sm mb-4 font-heading uppercase tracking-wider">
        Proud Sponsors
      </p>
      <div className="flex flex-wrap items-center justify-center gap-8">
        {sponsors.map((sponsor) => (
          <a
            key={sponsor.id}
            href={sponsor.website_url || '#'}
            target={sponsor.website_url ? '_blank' : '_self'}
            rel="noopener noreferrer"
            className="group transition-transform hover:scale-105"
            title={sponsor.name}
            data-testid={`sponsor-logo-${sponsor.id}`}
          >
            <div className="h-12 md:h-16 w-24 md:w-32 flex items-center justify-center opacity-70 group-hover:opacity-100 transition-opacity">
              <img 
                src={sponsor.image_url} 
                alt={sponsor.name}
                className="max-h-full max-w-full object-contain filter grayscale group-hover:grayscale-0 transition-all"
              />
            </div>
          </a>
        ))}
      </div>
    </div>
  );
};

// Export a helper hook to check if sponsors exist
export const useHasSponsors = () => {
  const [hasSponsors, setHasSponsors] = useState(false);

  useEffect(() => {
    const checkSponsors = async () => {
      try {
        const response = await axios.get(`${API_URL}/sponsors`);
        setHasSponsors(response.data.length > 0);
      } catch (error) {
        setHasSponsors(false);
      }
    };
    checkSponsors();
  }, []);

  return hasSponsors;
};
